import {
    GeoJSONGeometryType,
    GeospatialEntitySymbolType,
    SpatialEntityData,
} from '../../../../../pages/geospatial-viewer/model/api';
import {PointLayerService} from './engin-data/point-layer.service';
import {PolylineLayerService} from './engin-data/polyline-layer.service';
import {ProjectLayerService} from './custom-layer/project-layer.service';
import {Injectable} from '@angular/core';
import {GeospatialService} from '../helper/geospatial-service';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {ProjectLayerData} from '../../../../../pages/geospatial-viewer/model/project';
import {Project} from '@core/interfaces/engin/program-management/project';
import {VisualizationColorCode, VisualizationType} from '../../../../../pages/geospatial-viewer/model/visualization';
import {GeospatialTooltipType} from '../../../../../pages/geospatial-viewer/model/tooltip.model';
import {
    SpatialEntityDataExtended,
    SpatialOutageEntityDataExtended,
} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {OutageEntityDto} from '../../../../../pages/geospatial-viewer/layers/outage-layer';
import {OutagePointLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/outage-point-layer.service';
import {OutagePolylineLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/outage-polyline-layer.service';
import {PolygonLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/polygon-layer.service';
import {BucketDto} from '../../../../../pages/geospatial-viewer/layers/metric-layer';
import {SymbolLayerService} from '@core/utils/engin/geospatial-viewer/esri-layers/engin-data/symbol-layer.service';

export class AssetLayers {
    layers: FeatureLayer[];
}

// Everything is option, to allow for default settings with optional override
export interface LayerSettings {
    showTooltip?: boolean; // Render tooltip on click
    tooltipType?: GeospatialTooltipType;
    suppressTooltipTitle?: boolean; // Suppress default title in tooltip
    metrics?: any[]; // Metrics for tooltip, if TooltipType = Metric

    showStaticLabel?: boolean; // Show static label
    staticLabelProps?: any; // Static label properties, if showStaticLabel = true
}

/**
 * Top-level layer creation for ENGIN data layers (i.e. asset data with different view modes).
 * These functions orchestrate internal calls based on view type etc.
 */
@Injectable()
export class GenerateLayerService {
    constructor(
        private geospatialService: GeospatialService,
        private pointLayerService: PointLayerService,
        private polylineLayerService: PolylineLayerService,
        private outagePointLayerService: OutagePointLayerService,
        private outagePolylineLayerService: OutagePolylineLayerService,
        private polygonLayerService: PolygonLayerService,
        private projectLayerService: ProjectLayerService,
        private symbolLayerService: SymbolLayerService,
    ) {}

    /**
     * Create point and polyline layers for assets: metrics, registry.
     * @return AssetLayers
     */
    public createAssetFeatureLayers(
        data: SpatialEntityData[],
        renderedVisualizationType: VisualizationType,
        theme: string,
        settingsPoint?: LayerSettings,
        settingsLine?: LayerSettings,
    ): AssetLayers {
        const layers: FeatureLayer[] = [];
        const preparedData: SpatialEntityDataExtended[] = this.geospatialService.prepareEnginData(
            data,
            GeospatialTooltipType.METRIC_ASSET,
            renderedVisualizationType,
        );
        // Prepare point and linear data lists
        const pointData: SpatialEntityDataExtended[] = preparedData.filter(
            (a) => a.geoJsonGeometry.type === GeoJSONGeometryType.Point,
        );
        const linearData: SpatialEntityDataExtended[] = preparedData.filter(
            (a) =>
                a.geoJsonGeometry.type === GeoJSONGeometryType.LineString ||
                a.geoJsonGeometry.type === GeoJSONGeometryType.MultiLineString,
        );
        const polygonData: SpatialEntityDataExtended[] = preparedData.filter(
            (a) =>
                a.geoJsonGeometry.type === GeoJSONGeometryType.Polygon ||
                a.geoJsonGeometry.type === GeoJSONGeometryType.MultiPolygon,
        );

        // Create  point layers (point layer and overlay for overlapping points) and polyline layer
        if (pointData.length > 0) {
            const pointLayers = this.pointLayerService.preparePointLayers(
                pointData,
                renderedVisualizationType,
                'asset_point_layer',
                theme,
                settingsPoint,
            );
            layers.push(...pointLayers);
        }
        if (linearData.length > 0) {
            const linearLayer = this.polylineLayerService.preparePolylineLayer(
                linearData,
                renderedVisualizationType,
                'asset_line_layer',
                theme,
                settingsLine,
            );
            layers.push(linearLayer);
        }
        if (polygonData.length > 0) {
            const polygonLayer = this.polygonLayerService.preparePolygonLayer(
                polygonData,
                renderedVisualizationType,
                'asset_polygon_layer',
                theme,
                settingsLine,
            );
            layers.push(polygonLayer);
        }

        return {
            layers: [...layers],
        };
    }

    /**
     * Create polyline layers for asset groups: metrics, registry.
     * @return AssetLayers
     */
    public createGroupAssetFeatureLayers(
        data: SpatialEntityData[],
        renderedVisualizationType: VisualizationType,
        theme: string,
        settingsLine?: LayerSettings,
    ): AssetLayers {
        const layers: FeatureLayer[] = [];
        const preparedData: SpatialEntityDataExtended[] = this.geospatialService.prepareEnginData(
            data,
            settingsLine?.showTooltip ? settingsLine?.tooltipType : GeospatialTooltipType.NONE,
            renderedVisualizationType,
        );
        // Prepare point and linear data lists
        const linearData: SpatialEntityDataExtended[] = preparedData.filter(
            (a) =>
                a.geoJsonGeometry.type === GeoJSONGeometryType.LineString ||
                a.geoJsonGeometry.type === GeoJSONGeometryType.MultiLineString,
        );

        //create a polylineLayer
        if (linearData.length > 0) {
            const linearLayer = this.polylineLayerService.preparePolylineLayer(
                linearData,
                renderedVisualizationType,
                'group_line_layer',
                theme,
                settingsLine,
            );
            layers.push(linearLayer);
        }

        return {
            layers: [...layers],
        };
    }

    /**
     * Generate project layer for map
     * @private
     */
    public createProjectLayer(
        projects: Project[],
        currentUserId: string,
        colorCodeType?: VisualizationColorCode,
        projectCategories?: BucketDto[],
    ): FeatureLayer {
        const preparedData: ProjectLayerData[] = this.geospatialService.prepareProjectData(projects, currentUserId);
        const projectData: ProjectLayerData[] = this.projectLayerService.filterProjectsByCategory(
            preparedData,
            colorCodeType,
            projectCategories,
        );
        if (projectData.length === 0) return null;
        return this.projectLayerService.prepareProjectLayer(projectData, currentUserId, 'project', colorCodeType);
    }

    /**
     * Create layers for outages. This will create four layers:
     * - GeoJSONGeometry.Point: add features to point layer
     * - GeoJSONGeometry.LineString, MultiLineString: add features to line layers (2), and a separate point layer (1, centroid)
     * @return AssetLayers
     */
    public createOutageFeatureLayers(
        data: OutageEntityDto[],
        renderedVisualizationType: VisualizationType,
        theme: string,
        numMetricLayers: number,
        settings?: any,
    ): AssetLayers {
        const layers: FeatureLayer[] = [];
        const preparedData: SpatialOutageEntityDataExtended[] = this.geospatialService.prepareOutageData(
            data,
            GeospatialTooltipType.OUTAGE,
            renderedVisualizationType,
        );
        // Prepare point and linear data lists
        const pointData: SpatialOutageEntityDataExtended[] = preparedData.filter(
            (a) => a.geoJsonGeometry.type === GeoJSONGeometryType.Point,
        );
        const linearData: SpatialOutageEntityDataExtended[] = preparedData.filter(
            (a) =>
                a.geoJsonGeometry.type === GeoJSONGeometryType.LineString ||
                a.geoJsonGeometry.type === GeoJSONGeometryType.MultiLineString,
        );

        /*
         * Prepare (1) layer for point features
         * - Symbol: none
         * - Label: display at the point location, with different colour and border radius
         */
        if (pointData.length > 0) {
            const pointLayer = this.outagePointLayerService.preparePointLayer(
                pointData,
                renderedVisualizationType,
                'outage_point_layer',
                theme,
                settings,
            );
            layers.push(pointLayer);
        }
        if (linearData.length > 0) {
            const linearLayers = this.outagePolylineLayerService.preparePolylineLayer(
                linearData,
                renderedVisualizationType,
                'outage_line_layer',
                theme,
                numMetricLayers,
                settings,
            );
            layers.push(...linearLayers);
        }

        return {
            layers: [...layers],
        };
    }
    public createSubstationFeatureLayers(
        data: SpatialEntityData[],
        renderedVisualizationType: VisualizationType,
        theme: string,
        settingsPoint?: LayerSettings,
    ): {layers: FeatureLayer[]} {
        const layers: FeatureLayer[] = [];
        const preparedData: SpatialEntityDataExtended[] = this.geospatialService.prepareEnginData(
            data,
            GeospatialTooltipType.METRIC_SUBSTATION,
            renderedVisualizationType,
        );
        // Prepare substation data list
        const substationData: SpatialEntityDataExtended[] = preparedData.filter(
            (a) =>
                a.geoJsonGeometry.type === GeoJSONGeometryType.Point &&
                a.geospatialEntitySymbolType === GeospatialEntitySymbolType.SUBSTATION,
        );

        // Create substation symbol layers
        if (substationData.length > 0) {
            const symbolLayers = this.symbolLayerService.prepareSymbolLayers(
                substationData,
                renderedVisualizationType,
                'substation-symbol-layer',
                theme,
                GeospatialEntitySymbolType.SUBSTATION,
                settingsPoint,
            );
            layers.push(...symbolLayers);
        }

        return {
            layers: [...layers],
        };
    }
}
